// ────────────────────────────────────────────────────────────────────────────
// Bundled tax data — IRS, CMS, SSA tables. Accurate as of publication dates
// cited below. All numbers are in nominal year-specific dollars, but Cairn's
// projection works in today's (real) dollars so we apply the CURRENT-year
// table across the whole horizon (inflation-adjusted brackets would cancel
// out inflation-adjusted income). When Congress changes the law (SECURE,
// TCJA sunset, OBBBA, etc.), update this file in place.
//
// Sources:
//   • IRS Rev. Proc. 2025-32 (Oct 2025) — 2026 inflation adjustments, via
//     https://taxfoundation.org/data/all/federal/2026-tax-brackets/
//   • CMS 2026 Medicare premiums — https://www.cms.gov/newsroom
//   • SSA provisional-income thresholds (unindexed since 1984)
//   • IRS Uniform Lifetime Table (Reg. §1.401(a)(9)-9, 2022 revision)
// ────────────────────────────────────────────────────────────────────────────

export type FilingStatus = 'single' | 'married';

export interface Bracket {
  /** Income threshold above which this rate applies (for the amount over this floor). */
  over: number;
  rate: number;
}

/**
 * Progressive ordinary-income tax brackets for tax year 2026 (Single and MFJ).
 * Each entry is the floor and the rate applied to income in that slice.
 * Interpretation: find the last bracket whose `over` ≤ income; stack up
 * marginal rates over the slices (see `federalOrdinaryTax`).
 */
export const FEDERAL_ORDINARY_BRACKETS_2026: Record<FilingStatus, Bracket[]> = {
  single: [
    { over: 0, rate: 0.10 },
    { over: 12_400, rate: 0.12 },
    { over: 50_400, rate: 0.22 },
    { over: 105_700, rate: 0.24 },
    { over: 201_775, rate: 0.32 },
    { over: 256_225, rate: 0.35 },
    { over: 640_600, rate: 0.37 },
  ],
  married: [
    { over: 0, rate: 0.10 },
    { over: 24_800, rate: 0.12 },
    { over: 100_800, rate: 0.22 },
    { over: 211_400, rate: 0.24 },
    { over: 403_550, rate: 0.32 },
    { over: 512_450, rate: 0.35 },
    { over: 768_700, rate: 0.37 },
  ],
};

/**
 * Long-term capital gains / qualified dividend brackets for 2026.
 * LTCG is taxed at 0% up to the taxpayer's ordinary-income-adjusted threshold,
 * then 15%, then 20% above the second threshold.
 */
export const LTCG_BRACKETS_2026: Record<FilingStatus, { zero: number; fifteen: number }> = {
  // zero = 0%-bracket ceiling (LTCG below this ceiling, net of ordinary income, is untaxed)
  // fifteen = 15%-bracket ceiling (above it: 20% on the overage)
  single: { zero: 49_450, fifteen: 545_500 },
  married: { zero: 98_900, fifteen: 613_700 },
};

export const STANDARD_DEDUCTION_2026: Record<FilingStatus, number> = {
  single: 16_100,
  married: 32_200,
};

// ── FICA (2026 estimates — SSA announces final in Oct) ──────────────────────

export const FICA_2026 = {
  /** Employee-side SS tax rate. */
  ssRate: 0.062,
  /** 2026 SS wage base. SSA 2025-10-10 fact sheet. */
  ssWageBase: 184_500,
  /** Medicare rate (employee). */
  medicareRate: 0.0145,
  /** Additional-Medicare 0.9% surtax threshold on earned income. */
  additionalMedicareThreshold: { single: 200_000, married: 250_000 } as Record<FilingStatus, number>,
  additionalMedicareRate: 0.009,
} as const;

// ── Net Investment Income Tax (NIIT, §1411) ─────────────────────────────────
// Thresholds are NOT indexed for inflation (set by statute in 2013).

export const NIIT = {
  rate: 0.038,
  threshold: { single: 200_000, married: 250_000 } as Record<FilingStatus, number>,
};

// ── Social Security benefit taxation (unindexed since 1984) ─────────────────

export const SS_TAX_THRESHOLDS = {
  /** Provisional income = AGI + tax-exempt interest + 50% of SS benefits. */
  single: { fiftyPct: 25_000, eightyFivePct: 34_000 },
  married: { fiftyPct: 32_000, eightyFivePct: 44_000 },
} as const;

// ── IRMAA 2026 (Medicare Part B + Part D surcharges) ────────────────────────
//
// Part B premiums scale as a multiplier of the standard ($202.90/mo in 2026).
// Part D surcharges are added on top of whatever the plan's base premium is.
// Both tables are based on MAGI from TWO YEARS ago (i.e., 2026 IRMAA uses
// 2024 MAGI) — the engine simplifies by using the CURRENT-year MAGI, which
// is a reasonable approximation for long-horizon planning.

export interface IrmaaTier {
  /** MAGI cap for the tier (exclusive upper bound). Infinity for the top. */
  singleCap: number;
  marriedCap: number;
  /** Total Part B premium per person per month. */
  partBMonthly: number;
  /** Part D surcharge per person per month (added to plan premium). */
  partDSurcharge: number;
}

export const IRMAA_2026: IrmaaTier[] = [
  // Standard tier (no surcharge)
  { singleCap: 109_000, marriedCap: 218_000, partBMonthly: 202.90, partDSurcharge: 0 },
  { singleCap: 137_000, marriedCap: 274_000, partBMonthly: 284.10, partDSurcharge: 14.50 },
  { singleCap: 171_000, marriedCap: 342_000, partBMonthly: 405.80, partDSurcharge: 37.50 },
  { singleCap: 205_000, marriedCap: 410_000, partBMonthly: 527.50, partDSurcharge: 60.40 },
  { singleCap: 500_000, marriedCap: 750_000, partBMonthly: 649.20, partDSurcharge: 83.30 },
  { singleCap: Infinity, marriedCap: Infinity, partBMonthly: 689.90, partDSurcharge: 91.00 },
];

// ── Required Minimum Distributions (IRS Uniform Lifetime Table, post-2022) ──
// Age 73 is the current RMD start age (SECURE Act 2.0 2022); rises to 75 in
// 2033 for those born 1960+. The engine uses 73 as the universal start;
// users in that transition band can approximate by delaying the RMD event.

export const RMD_START_AGE = 73;

/** Divisor for the IRS Uniform Lifetime Table. Balance ÷ divisor = RMD.
 *  Source: Treas. Reg. §1.401(a)(9)-9, 2022 revision. */
export const RMD_DIVISORS: Record<number, number> = {
  73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1,
  80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2,
  87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1,
  94: 9.5, 95: 8.9, 96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4,
  101: 6.0, 102: 5.6, 103: 5.2, 104: 4.9, 105: 4.6, 106: 4.3, 107: 4.1,
  108: 3.9, 109: 3.7, 110: 3.5, 111: 3.4, 112: 3.3, 113: 3.1, 114: 3.0,
  115: 2.9, 116: 2.8, 117: 2.7, 118: 2.5, 119: 2.3, 120: 2.0,
};

// ── 2026 Defined-Contribution plan limits (IRS Rev. Proc. 2025-32) ─────────
// 402(g) caps employee elective deferrals across pretax + Roth 401(k).
// 415(c) caps "total annual additions" — employee + employer + after-tax.
// IRA limit is the separate ceiling on direct IRA / Roth-IRA contributions.
//
// SECURE 2.0 introduced an enhanced "super catch-up" for ages 60-63 (in
// addition to the standard age-50+ catch-up). Outside that window the
// standard catch-up applies; below age 50, no catch-up.

// 2026 limits per IRS Rev. Proc. 2025-32 (the inflation-adjusted update from
// the 2025 baseline of $23,500 / $70,000 / $7,000). The 402(g) elective
// deferral cap rose to $24,500; the 415(c) total annual additions cap rose
// to $72,000. Catch-ups + IRA limit unchanged for 2026.
export const IRS_402G_LIMIT_2026 = 24_500;
export const IRS_402G_CATCHUP_50_2026 = 7_500;
export const IRS_402G_SUPER_CATCHUP_60_63_2026 = 11_250; // SECURE 2.0
export const IRS_415C_LIMIT_2026 = 72_000;
export const IRS_415C_CATCHUP_50_2026 = 7_500;
export const IRS_IRA_LIMIT_2026 = 7_000;
export const IRS_IRA_CATCHUP_50_2026 = 1_000;

/** 402(g) elective-deferral limit for the user's age. Combines base limit
 *  with the standard age-50 catch-up and the SECURE 2.0 super-catch-up
 *  (only available 60-63 inclusive). */
export function electiveDeferralLimit(age: number): number {
  let limit = IRS_402G_LIMIT_2026;
  if (age >= 50) limit += IRS_402G_CATCHUP_50_2026;
  if (age >= 60 && age <= 63) {
    // Super-catch-up REPLACES the standard 50+ catch-up for 60-63 since the
    // statute defines it as the greater of $11,250 or 150% of the standard
    // catch-up. Effectively: base + super (not base + standard + super).
    limit = IRS_402G_LIMIT_2026 + IRS_402G_SUPER_CATCHUP_60_63_2026;
  }
  return limit;
}

/** 415(c) "annual additions" cap: employee + employer + after-tax. The
 *  age-50 catch-up applies on top because it sits outside the 415(c) limit
 *  (it's a separate elective-deferral allowance). */
export function annualAdditionsLimit(age: number): number {
  let limit = IRS_415C_LIMIT_2026;
  if (age >= 50) limit += IRS_415C_CATCHUP_50_2026;
  if (age >= 60 && age <= 63) {
    // Same super-catch-up logic as 402(g): replace standard with super.
    limit = IRS_415C_LIMIT_2026 + IRS_402G_SUPER_CATCHUP_60_63_2026;
  }
  return limit;
}

/** Direct IRA / Roth-IRA contribution limit for the user's age. */
export function iraContributionLimit(age: number): number {
  return IRS_IRA_LIMIT_2026 + (age >= 50 ? IRS_IRA_CATCHUP_50_2026 : 0);
}

// ── ACA Federal Poverty Level thresholds (48 states, 2026 estimate) ────────
// Source: HHS poverty guidelines, inflation-adjusted. Household of 1 = base;
// each additional person adds the marginal amount.

export const FPL_2026 = {
  singleHousehold: 16_150,
  perAdditionalPerson: 5_700,
};

/** Household FPL for a given size (48-states). */
export function fpl(householdSize: number): number {
  return FPL_2026.singleHousehold + (householdSize - 1) * FPL_2026.perAdditionalPerson;
}
